'use client';
import React, {
    createContext,
    useContext,
    useMemo,
    useCallback,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import { ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_SITE_CONTENT_ELEMENT_LISTING_QUERY,
    getSiteContentElementListingQueryVariables,
} from '../queries/getSiteContentElementListingQuery';
import type { GetSiteContentElementListingQuery } from '../schemas/generated-types/graphql';
import { transformToContentTree } from '../tools/transformSiteContentElementListingResponseToTree';
import type { ContentTree } from '../types/SiteContentElement';
import { getSiteContentPlaceholder } from '../factories/getSiteContentPlaceholder';
import type { MergeTreeError } from '../tools/validateSiteContentElementTree';
import { mergeTrees, getMaxErrorSeverity } from '../tools/validateSiteContentElementTree';
import { mapTreeToPaths, prefixTreeTexts, DEBUG_LEVELS } from '../tools/debugMapSiteContentTree';
import type { DebugLevel } from '../tools/debugMapSiteContentTree';
import { isSiteContentPath } from '../guards/siteContentPath';
import { fetchFallbackQueryResponse } from '../../../common/tools/fallbackQueryTools';

// Entry state for each registered path
interface SiteContentEntry {
    contentTree: ContentTree;
    loading: boolean;
    error: any;
    loaded: boolean;
    requestedLocale: string | null;
}

function useImmediateReducer<S, A>(
    reducer: (state: S, action: A) => S,
    initialState: S
): [S, (action: A) => void, React.MutableRefObject<S>] {
    const stateRef = useRef<S>(initialState);
    const [, setTick] = useState(0);

    const dispatch = useCallback(
        (action: A) => {
            stateRef.current = reducer(stateRef.current, action);
            setTick((t) => t + 1);
        },
        [reducer]
    );

    return [stateRef.current, dispatch, stateRef];
}

// Actions for reducer
type Action =
    | { type: 'loadStart'; path: string; locale: string }
    | { type: 'loadError'; path: string; error: any }
    | { type: 'loadFinalize'; path: string; tree: ContentTree; locale: string };

// Reducer to manage entries map
function reducer(
    state: Record<string, SiteContentEntry>,
    action: Action
): Record<string, SiteContentEntry> {
    const newState = { ...state };
    switch (action.type) {
        case 'loadStart':
            const placeholder: ContentTree = isSiteContentPath(action.path)
                ? getSiteContentPlaceholder(action.path)
                : (console.warn(`Unsupported site content path: ${action.path}`), {});
            newState[action.path] = {
                contentTree: placeholder,
                loading: true,
                error: null,
                loaded: false,
                requestedLocale: action.locale,
            };
            return newState;
        case 'loadError':
            newState[action.path] = {
                contentTree: newState[action.path]?.contentTree || {},
                loading: newState[action.path]?.loading || false,
                error: action.error,
                loaded: newState[action.path]?.loaded || false,
                requestedLocale: newState[action.path]?.requestedLocale ?? null,
            };
            return newState;
        case 'loadFinalize':
            newState[action.path] = {
                contentTree: action.tree,
                loading: false,
                error: newState[action.path]?.error || null,
                loaded: true,
                requestedLocale: action.locale,
            };
            return newState;
        default:
            return state;
    }
}

interface RegistryContextValue {
    get: (path: string) => SiteContentEntry | undefined;
    fetch: (path: string) => void;
}

const SiteContentRegistryContext = createContext<RegistryContextValue | undefined>(undefined);

// Helper to get subtree for nested paths
function getSubtree(tree: ContentTree, subpath: string): ContentTree {
    if (!subpath) return tree;
    const [head, ...rest] = subpath.split('/');
    if (!head || !(head in tree)) return {};
    const node = tree[head];
    if (!node || typeof node !== 'object') return {};
    return getSubtree(node as ContentTree, rest.join('/'));
}

export interface SiteContentRegistryProviderProps {
    apolloClient: ApolloClient<any>;
    preloadPaths?: string[];
    children: ReactNode;
}

// Declare our debug globals
declare global {
    interface Window {
        __DEBUG_PIMCORE_PATH_PLACEHOLDERS?: DebugLevel;
        togglePimcoreDebugPaths?: (lvl?: DebugLevel) => void;
    }
}

/**
 * Provider that holds and manages content entries and fetch logic for any Pimcore path.
 */
export const SiteContentRegistryProvider: React.FC<SiteContentRegistryProviderProps> = ({
    apolloClient,
    preloadPaths = [],
    children,
}) => {
    const { locale } = useLocale();
    const client = apolloClient;
    const [state, dispatch, stateRef] = useImmediateReducer(reducer, {});

    // Debug mode for visualizing Pimcore paths in the UI
    // ✅ SSR 安全的初始化：不要在初始 render 直接讀 window
    const [debugLevel, setDebugLevel] = useState<DebugLevel>('none');
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setDebugLevel(window.__DEBUG_PIMCORE_PATH_PLACEHOLDERS ?? 'none');
        }
    }, []);

    // Fetch function always has current locale
    const fetchContent = useCallback(
        async (path: string) => {
            console.debug(
                `[SiteContentRegistryProvider] Fetching content for path: ${path}, locale: ${locale}`
            );
            dispatch({ type: 'loadStart', path, locale });
            let rawData: any = null;
            const query = GET_SITE_CONTENT_ELEMENT_LISTING_QUERY;
            try {
                // throw new Error("Simulated error to test using local JSON as fallback");
                const { data } = await client.query<GetSiteContentElementListingQuery>({
                    query,
                    variables: getSiteContentElementListingQueryVariables(locale, path),
                    // fetchPolicy: 'cache-first', // some items are not refreshed
                    fetchPolicy: 'network-only',
                });
                rawData = data ?? null;
            } catch (networkError) {
                console.error(
                    `Network query failed for ${query.fallbackKey} path ${path}, falling back to hard-coded data`,
                    networkError
                );
                if (isSiteContentPath(path)) {
                    // fallback to local JSON
                    try {
                        const fallback = await fetchFallbackQueryResponse(
                            query.fallbackKey,
                            locale,
                            path
                        );
                        rawData = fallback;
                    } catch (fallbackError) {
                        // we are in trouble, empty placeholder is going to be returned
                        console.error(`Fallback JSON load failed for ${path}:`, fallbackError);
                    }
                }
            }
            // Transform the raw response into a content tree
            const rawTree = transformToContentTree(rawData);
            // If we got no data, mark as error and return placeholder data
            if (!rawTree || Object.keys(rawTree).length === 0) {
                const nextState = reducer(stateRef.current, {
                    type: 'loadError',
                    path,
                    error: new Error('No data returned'),
                });
                stateRef.current = nextState;
                dispatch({ type: 'loadError', path, error: new Error('Data failed to load') });
            }
            // Merge with placeholder to ensure expected structure (e.g., HomePage, Footer)
            const mergeErrors: MergeTreeError[] = [];
            const placeholder: ContentTree = isSiteContentPath(path)
                ? getSiteContentPlaceholder(path)
                : (console.warn(`Unsupported site content path: ${path}`), {});
            let tree =
                Object.keys(placeholder).length > 0
                    ? mergeTrees(placeholder, rawTree, mergeErrors)
                    : rawTree;
            // Log any merge errors for debugging
            if (mergeErrors.length > 0) {
                const maxErrorSeverity = getMaxErrorSeverity(mergeErrors);
                console.group(`Errors merging content tree for path "${path}":`);

                mergeErrors.forEach((err) => {
                    console[err.severity](
                        `At "${err.path}": ${err.message}\n`,
                        err.placeholder !== undefined ? ['Placeholder:', err.placeholder] : [],
                        'Actual kind:',
                        err.actualKind,
                        'Actual value:',
                        err.actualValue
                    );
                });
                // Log more context if severity is 'error'
                if (maxErrorSeverity === 'error') {
                    console.log(`Raw data for path "${path}":`, rawData);
                    console.log(`Raw tree for path "${path}":`, rawTree);
                    console.log(`Placeholder for path "${path}":`, placeholder);
                    console.log(`Merged tree for path "${path}":`, tree);
                }
                console.groupEnd();
            }
            if (debugLevel === 'prefix') {
                tree = prefixTreeTexts(tree);
            } else if (debugLevel === 'shortPath' || debugLevel === 'fullPath') {
                tree = mapTreeToPaths(
                    tree,
                    path.replace(/^\//, '').split('/').join('.'),
                    debugLevel
                );
            }

            const nextState = reducer(stateRef.current, {
                type: 'loadFinalize',
                path,
                tree,
                locale,
            });
            stateRef.current = nextState;
            dispatch({ type: 'loadFinalize', path, tree, locale });
        },
        [client, locale, debugLevel, dispatch, stateRef]
    );

    useEffect(() => {
        window.togglePimcoreDebugPaths = (lvl?: DebugLevel) => {
            let next: DebugLevel;

            if (lvl !== undefined) {
                // validate literal
                if (!DEBUG_LEVELS.includes(lvl)) {
                    console.error(
                        `Invalid debug level “${lvl}”. Must be one of: ${DEBUG_LEVELS.join(', ')}.`
                    );
                    return;
                }
                next = lvl;
            } else {
                // cycle
                const currentIndex = DEBUG_LEVELS.indexOf(debugLevel);
                const nextIndex = (currentIndex + 1) % DEBUG_LEVELS.length;
                const candidate = DEBUG_LEVELS[nextIndex];
                if (!candidate) {
                    console.error(
                        `Cannot cycle debug level from “${debugLevel}” (index ${currentIndex})`
                    );
                    return;
                }
                next = candidate;
            }

            window.__DEBUG_PIMCORE_PATH_PLACEHOLDERS = next;
            setDebugLevel(next);
            console.info(`Pimcore debug mode → ${next}`);
        };
    }, [debugLevel]);

    // as soon as debugLevel updates, re-fetch all already-loaded paths
    useEffect(() => {
        Object.keys(stateRef.current).forEach((path) => {
            if (stateRef.current[path]?.loaded) {
                console.debug(
                    `[SiteContentRegistryProvider] Reloading path "${path}" due to debug level change`
                );
                fetchContent(path);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debugLevel]);

    // Whenever the locale changes, ensure every registered path has been fetched for that locale
    useEffect(() => {
        Object.entries(stateRef.current).forEach(([path, entry]) => {
            if (entry.requestedLocale === locale) {
                return;
            }
            console.debug(
                `[SiteContentRegistryProvider] Reloading path "${path}" due to locale change`
            );
            fetchContent(path);
        });
        // We intentionally omit fetchContent from deps so we don't loop infinitely
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale]);

    // Registry methods
    const registry = useMemo<RegistryContextValue>(
        () => ({
            get(path: string) {
                // exact entry
                const currentState = stateRef.current;
                if (currentState[path]) {
                    return currentState[path];
                }
                // find the longest matching superpath
                const superpath = Object.keys(state)
                    .filter((p) => path.startsWith(p + '/'))
                    .sort((a, b) => b.length - a.length)[0];
                if (superpath) {
                    const entry = state[superpath]!;
                    const subpath = path.slice(superpath.length + 1);
                    return {
                        ...entry,
                        contentTree: getSubtree(entry.contentTree, subpath),
                    };
                }
                return undefined;
            },
            fetch(path: string) {
                console.debug(`[SiteContentRegistryProvider] Requesting fetch for path: ${path}`);
                fetchContent(path);
            },
        }),
        [state, fetchContent, stateRef]
    );

    useEffect(() => {
        if (preloadPaths.length === 0) return;
        preloadPaths.forEach((path) => {
            const entry = stateRef.current[path];
            if (!entry?.loading && entry?.loaded) {
                console.debug(`[SiteContentRegistryProvider] Preloading path: ${path}`);
                fetchContent(path);
            }
        });

        // re-run when preloadPaths or fetchContent change (i.e. on locale or client change).
        // we intentionally omit `state` from deps so that we don't loop on every dispatch
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preloadPaths, fetchContent]);

    return (
        <SiteContentRegistryContext.Provider value={registry}>
            {children}
        </SiteContentRegistryContext.Provider>
    );
};

/**
 * Hook to access and lazily initialize content for a specific Pimcore path.
 * Returns a placeholder entry immediately if nothing is in the registry yet,
 * then kicks off the real fetch in an effect.
 */
export function useSiteContentElementsAt(path: string): SiteContentEntry {
    const registry = useContext(SiteContentRegistryContext);
    if (!registry) {
        throw new Error('SiteContentRegistryProvider is missing in the tree');
    }

    const entry = registry.get(path);

    // schedule a fetch (which will dispatch loadStart immediately) if we had no entry
    useEffect(() => {
        if (!entry) {
            registry.fetch(path);
        }
    }, [entry, path, registry]);

    // if the registry had an entry, return it
    if (entry) {
        return entry;
    }

    // otherwise, synthesize the same placeholder the registry would have dispatched
    // so consumers can render a “loading” state without error
    // This placeholder will be used only for first render, because we immediately scheduled
    // a fetch above, which will update the entry in the registry and cause a re-render
    const placeholder: SiteContentEntry = {
        contentTree: isSiteContentPath(path) ? getSiteContentPlaceholder(path) : {},
        loading: true,
        error: null,
        loaded: false,
        requestedLocale: null,
    };
    return placeholder;
}
